// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Utility for parsing large arrays in chunks to prevent UI thread blocking.
 * Uses requestIdleCallback when available, falls back to setTimeout.
 */
import {
  type SchemaValidationOptions,
  handleSchemaValidationError,
} from '@/utilities/schema-validation';
import { z } from 'zod';

const CHUNK_SIZE = 1000; // Assets per chunk

interface ChunkedParseOptions {
  chunkSize?: number;
  schemaValidation?: SchemaValidationOptions;
}

/**
 * Yields control back to the browser between chunk processing.
 * Uses requestIdleCallback for better scheduling when available.
 */
function yieldToMain(): Promise<void> {
  return new Promise((resolve) => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(
        () => {
          resolve();
        },
        { timeout: 50 }
      );
    } else {
      setTimeout(resolve, 0);
    }
  });
}

/**
 * Parses an array of items in chunks, yielding to the main thread between chunks.
 * Prevents UI freezing when parsing large datasets.
 *
 * @param items - Array of items to parse
 * @param itemSchema - Zod schema for individual items
 * @param options - Optional chunk size and schema validation reporting context
 * @returns Promise resolving to parsed items array
 */
export async function parseArrayInChunks<T extends z.ZodTypeAny>(
  items: unknown[],
  itemSchema: T,
  options: ChunkedParseOptions = {}
): Promise<z.infer<T>[]> {
  const results: z.infer<T>[] = [];
  const chunkSize = options.chunkSize ?? CHUNK_SIZE;

  for (let i = 0; i < items.length; i += chunkSize) {
    const chunk = items.slice(i, i + chunkSize);

    // Parse current chunk
    for (const [chunkIndex, item] of chunk.entries()) {
      try {
        results.push(itemSchema.parse(item) as z.infer<T>);
      } catch (error) {
        if (
          error instanceof z.ZodError &&
          options.schemaValidation !== undefined
        ) {
          handleSchemaValidationError(error, item, {
            ...options.schemaValidation,
            extra: {
              ...options.schemaValidation.extra,
              itemCount: items.length,
              itemIndex: i + chunkIndex,
            },
          });
        }

        throw error;
      }
    }

    // Yield to main thread after each chunk
    if (i + chunkSize < items.length) {
      await yieldToMain();
    }
  }

  return results;
}
